import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Database, 
  Plus, 
  Search, 
  Edit3, 
  Trash2, 
  Eye, 
  RefreshCw, 
  Code, 
  Check, 
  X, 
  AlertTriangle 
} from 'lucide-react';
import { 
  getCollectionDocs, 
  createCollectionDoc, 
  updateCollectionDoc, 
  deleteCollectionDoc 
} from '../../lib/adminApi';
import { useToast } from '../../hooks/use-toast';

const PRESET_COLLECTIONS = [
  { id: 'bookings', name: 'Bookings (Strategy Sessions)' },
  { id: 'lead_magnets', name: 'Lead Magnets' },
  { id: 'testimonials', name: 'Testimonials' },
  { id: 'contacts', name: 'Contacts & Leads' },
  { id: 'fna_reports', name: 'FNA Reports' },
  { id: 'reports', name: 'Premium Reports' },
  { id: 'tool_usage', name: 'Agent Tool Usage' }
];

export default function AdminDataExplorer({ initialCollection }) {
  const { toast } = useToast();
  const [selectedCol, setSelectedCol] = useState(initialCollection || 'bookings');
  const [customColInput, setCustomColInput] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Modals state
  const [viewDoc, setViewDoc] = useState(null);
  const [editDoc, setEditDoc] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Form JSON state
  const [jsonInput, setJsonInput] = useState('{}');
  const [formFields, setFormFields] = useState({});

  useEffect(() => {
    if (initialCollection) setSelectedCol(initialCollection);
  }, [initialCollection]);

  const loadDocs = async () => {
    setLoading(true);
    try {
      const docs = await getCollectionDocs(selectedCol);
      setDocuments(docs);
    } catch (e) {
      toast({
        title: 'Fetch Error',
        description: `Failed to load collection '${selectedCol}': ${e.message}`,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocs();
  }, [selectedCol]);

  const handleCustomColSubmit = (e) => {
    e.preventDefault();
    if (customColInput.trim()) {
      setSelectedCol(customColInput.trim());
      setCustomColInput('');
    }
  };

  // Filter documents by search query
  const filteredDocs = documents.filter((doc) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return JSON.stringify(doc).toLowerCase().includes(q);
  });

  // Open Create Modal
  const openCreateModal = () => {
    const sample = {
      email: '',
      firstname: '',
      lastname: '',
      status: 'active',
      createdAt: new Date().toISOString()
    };
    setFormFields(sample);
    setJsonInput(JSON.stringify(sample, null, 2));
    setIsCreating(true);
  };

  // Open Edit Modal
  const openEditModal = (docObj) => {
    setEditDoc(docObj);
    const copy = { ...docObj };
    delete copy._id;
    setFormFields(copy);
    setJsonInput(JSON.stringify(copy, null, 2));
  };

  // Save Create Document
  const handleCreateSubmit = async () => {
    try {
      let parsed = formFields;
      try {
        parsed = JSON.parse(jsonInput);
      } catch (e) {
        // use formFields if raw JSON parse fails
      }

      await createCollectionDoc(selectedCol, parsed);
      toast({
        title: 'Document Created 🎉',
        description: `Successfully added document to ${selectedCol}`,
        variant: 'success'
      });
      setIsCreating(false);
      loadDocs();
    } catch (e) {
      toast({
        title: 'Create Error',
        description: e.message,
        variant: 'destructive'
      });
    }
  };

  // Save Update Document
  const handleUpdateSubmit = async () => {
    if (!editDoc?._id) return;
    try {
      let parsed = formFields;
      try {
        parsed = JSON.parse(jsonInput);
      } catch (e) {
        // fallback to formFields
      }

      await updateCollectionDoc(selectedCol, editDoc._id, parsed);
      toast({
        title: 'Document Updated',
        description: `Successfully saved changes to doc ${editDoc._id}`,
        variant: 'success'
      });
      setEditDoc(null);
      loadDocs();
    } catch (e) {
      toast({
        title: 'Update Error',
        description: e.message,
        variant: 'destructive'
      });
    }
  };

  // Confirm Delete Document
  const handleDeleteConfirm = async () => {
    if (!deleteTarget?._id) return;
    try {
      await deleteCollectionDoc(selectedCol, deleteTarget._id);
      toast({
        title: 'Document Deleted',
        description: `Removed doc ${deleteTarget._id} from ${selectedCol}`,
        variant: 'success'
      });
      setDeleteTarget(null);
      loadDocs();
    } catch (e) {
      toast({
        title: 'Delete Error',
        description: e.message,
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Header & Collection Selector */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-[#0D1B2A]/90 border border-[#C9A84C]/30 p-6 rounded-2xl shadow-xl">
        <div>
          <h2 className="text-2xl font-bold font-playfair text-white flex items-center gap-2">
            <Database className="w-6 h-6 text-[#C9A84C]" />
            Firestore Data Explorer (CRUD)
          </h2>
          <p className="text-slate-400 text-xs sm:text-sm">Inspect, create, edit, or delete records in any Firestore collection.</p>
        </div>

        {/* Collection Dropdown & Custom Input */}
        <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
          <select
            value={PRESET_COLLECTIONS.some(c => c.id === selectedCol) ? selectedCol : 'custom'}
            onChange={(e) => {
              if (e.target.value !== 'custom') {
                setSelectedCol(e.target.value);
              }
            }}
            className="h-11 bg-[#162236] border border-[#C9A84C]/40 text-white rounded-xl px-4 text-sm font-semibold focus:border-[#C9A84C]"
          >
            {PRESET_COLLECTIONS.map(col => (
              <option key={col.id} value={col.id}>{col.name}</option>
            ))}
            <option value="custom">-- Custom Collection --</option>
          </select>

          <form onSubmit={handleCustomColSubmit} className="flex items-center gap-2">
            <Input
              type="text"
              placeholder="Or type collection name..."
              value={customColInput}
              onChange={(e) => setCustomColInput(e.target.value)}
              className="h-11 bg-[#162236] border border-[#C9A84C]/30 text-white placeholder:text-slate-500 rounded-xl text-xs sm:text-sm px-3"
            />
            <Button type="submit" size="sm" className="h-11 bg-[#101F33] hover:bg-[#1A2E4B] border border-[#C9A84C]/30 text-white px-3 rounded-xl font-semibold">
              Go
            </Button>
          </form>
        </div>
      </div>

      {/* Toolbar: Search, Refresh, Create Button */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <Input
            type="text"
            placeholder={`Search ${selectedCol}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-11 bg-[#0D1B2A] border border-[#C9A84C]/30 text-white pl-10 rounded-xl text-sm"
          />
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          <Button
            onClick={loadDocs}
            disabled={loading}
            className="h-11 bg-[#162236] hover:bg-[#1f2e48] text-slate-200 border border-[#C9A84C]/30 px-4 rounded-xl text-sm font-semibold flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 text-[#C9A84C] ${loading ? 'animate-spin' : ''}`} />
            Reload
          </Button>

          <Button
            onClick={openCreateModal}
            className="h-11 bg-gradient-to-r from-[#C9A84C] to-[#8C6D23] hover:brightness-110 text-[#070E1B] font-bold px-5 rounded-xl text-sm flex items-center gap-2 shadow-lg"
          >
            <Plus className="w-4 h-4" />
            Add Document
          </Button>
        </div>
      </div>

      {/* Main Data Table */}
      <Card className="bg-[#0D1B2A]/90 border border-[#C9A84C]/30 rounded-2xl shadow-xl overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 text-center text-slate-400 text-sm">Loading documents from <span className="text-[#E8D4A0] font-mono">{selectedCol}</span>...</div>
          ) : filteredDocs.length === 0 ? (
            <div className="py-16 text-center text-slate-400 space-y-2">
              <Database className="w-10 h-10 text-slate-600 mx-auto" />
              <p className="text-base font-semibold text-slate-300">No documents found in collection '{selectedCol}'</p>
              <p className="text-xs text-slate-500">Click "Add Document" above to insert a record.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs sm:text-sm text-slate-300">
                <thead className="bg-[#162236] text-[#E8D4A0] uppercase text-xs font-semibold tracking-wider border-b border-[#C9A84C]/20">
                  <tr>
                    <th className="py-4 px-4 font-mono">Document ID</th>
                    <th className="py-4 px-4">Primary Email / Name</th>
                    <th className="py-4 px-4">Key Summary Details</th>
                    <th className="py-4 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1e293b]">
                  {filteredDocs.map((docObj) => {
                    const primary = docObj.email || docObj.name || docObj.firstname ? `${docObj.firstname || ''} ${docObj.lastname || ''}` : docObj._id;
                    return (
                      <tr key={docObj._id} className="hover:bg-[#162236]/60 transition-colors">
                        <td className="py-3.5 px-4 font-mono text-[#C9A84C] font-semibold">
                          {docObj._id}
                        </td>
                        <td className="py-3.5 px-4 font-semibold text-white">
                          {primary}
                        </td>
                        <td className="py-3.5 px-4 text-slate-400 max-w-xs truncate font-mono text-xs">
                          {Object.entries(docObj)
                            .filter(([k]) => k !== '_id' && k !== 'email' && k !== 'firstname' && k !== 'lastname')
                            .slice(0, 3)
                            .map(([k, v]) => `${k}: ${typeof v === 'object' ? '...' : String(v)}`)
                            .join(' | ')}
                        </td>
                        <td className="py-3.5 px-4 text-right space-x-2 whitespace-nowrap">
                          <Button
                            size="sm"
                            onClick={() => setViewDoc(docObj)}
                            className="bg-[#162236] hover:bg-[#202f48] text-slate-300 border border-slate-700 px-2.5 py-1 text-xs rounded-lg"
                          >
                            <Eye className="w-3.5 h-3.5 text-blue-400" />
                          </Button>

                          <Button
                            size="sm"
                            onClick={() => openEditModal(docObj)}
                            className="bg-[#162236] hover:bg-[#202f48] text-slate-300 border border-slate-700 px-2.5 py-1 text-xs rounded-lg"
                          >
                            <Edit3 className="w-3.5 h-3.5 text-[#C9A84C]" />
                          </Button>

                          <Button
                            size="sm"
                            onClick={() => setDeleteTarget(docObj)}
                            className="bg-[#162236] hover:bg-red-950 text-red-400 border border-red-900/50 px-2.5 py-1 text-xs rounded-lg"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View JSON Detail Drawer / Modal */}
      {viewDoc && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0D1B2A] border border-[#C9A84C]/40 rounded-3xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
            <div className="p-5 bg-[#162236] border-b border-[#C9A84C]/30 flex justify-between items-center">
              <h3 className="text-lg font-bold font-playfair text-white flex items-center gap-2">
                <Code className="w-5 h-5 text-[#C9A84C]" />
                Document Details: <span className="font-mono text-[#E8D4A0] text-sm">{viewDoc._id}</span>
              </h3>
              <button onClick={() => setViewDoc(null)} className="text-slate-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto font-mono text-xs text-emerald-400 bg-[#070E1B] space-y-4">
              <pre className="whitespace-pre-wrap leading-relaxed">{JSON.stringify(viewDoc, null, 2)}</pre>
            </div>
            <div className="p-4 bg-[#162236] border-t border-[#C9A84C]/30 text-right">
              <Button onClick={() => setViewDoc(null)} className="bg-[#0D1B2A] text-white border border-slate-700">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Document Modal */}
      {(isCreating || editDoc) && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0D1B2A] border border-[#C9A84C]/40 rounded-3xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
            <div className="p-5 bg-[#162236] border-b border-[#C9A84C]/30 flex justify-between items-center">
              <h3 className="text-lg font-bold font-playfair text-white">
                {isCreating ? `Create Document in '${selectedCol}'` : `Edit Document: ${editDoc?._id}`}
              </h3>
              <button onClick={() => { setIsCreating(false); setEditDoc(null); }} className="text-slate-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto space-y-4">
              <Label className="text-xs uppercase font-bold text-slate-300">
                JSON Document Fields
              </Label>
              <textarea
                rows={12}
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                className="w-full bg-[#070E1B] border border-[#C9A84C]/30 text-emerald-400 font-mono text-xs p-4 rounded-xl focus:border-[#C9A84C]"
              />
            </div>
            <div className="p-4 bg-[#162236] border-t border-[#C9A84C]/30 flex justify-end gap-3">
              <Button onClick={() => { setIsCreating(false); setEditDoc(null); }} className="bg-[#0D1B2A] text-slate-300 border border-slate-700">
                Cancel
              </Button>
              <Button
                onClick={isCreating ? handleCreateSubmit : handleUpdateSubmit}
                className="bg-gradient-to-r from-[#C9A84C] to-[#8C6D23] text-[#070E1B] font-bold"
              >
                <Check className="w-4 h-4 mr-1" />
                {isCreating ? 'Create Record' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0D1B2A] border border-red-500/40 rounded-3xl max-w-md w-full p-6 text-center space-y-5 shadow-2xl">
            <div className="w-14 h-14 bg-red-950/80 border border-red-500 rounded-full flex items-center justify-center mx-auto text-red-400">
              <AlertTriangle className="w-7 h-7" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-bold font-playfair text-white">Confirm Deletion</h3>
              <p className="text-slate-300 text-xs sm:text-sm">
                Are you sure you want to delete document <strong className="text-red-400 font-mono">{deleteTarget._id}</strong> from collection <strong className="text-[#E8D4A0]">{selectedCol}</strong>?
              </p>
            </div>
            <div className="flex justify-center gap-3 pt-2">
              <Button onClick={() => setDeleteTarget(null)} className="bg-[#162236] text-slate-300 border border-slate-700">
                Cancel
              </Button>
              <Button onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700 text-white font-bold">
                Delete Permanently
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
